import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, TextField, Grid, Card, CardContent, LinearProgress, Avatar, AvatarGroup,
    Chip, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel,
    InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, ToggleButton, ToggleButtonGroup,
    FormControlLabel, Checkbox, Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PersonIcon from '@mui/icons-material/Person';
import Loading from '../components/Loading';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

const getStatusColor = (status) => {
    switch (status) {
        case 'Active': return 'primary';
        case 'Completed': return 'success';
        case 'In Progress': return 'primary';
        case 'On Hold': return 'warning';
        default: return 'default';
    }
};

const getPriorityColor = (priority) => {
    switch (priority) {
        case 'High': return '#DC3545';
        case 'Medium': return '#FFB020';
        case 'Low': return '#00C292';
        default: return '#6C757D';
    }
};

const calculateProgress = (tasks) => {
    if (!tasks || tasks.length === 0) return 0;
    const completed = tasks.filter(t => t.completed).length;
    return Math.round((completed / tasks.length) * 100);
};

function ITProjects() {
    const [projects, setProjects] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [newProject, setNewProject] = useState({
        name: '', description: '', status: 'Active', priority: 'Medium', time_limit: '', start_date: '', country_id: ''
    });
    const [viewMode, setViewMode] = useState('grid');
    const [filter, setFilter] = useState('All');
    const [search, setSearch] = useState('');
    const navigate = useNavigate();
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchProjects();
        fetchCountries();
    }, [search]);

    const fetchCountries = async () => {
        try {
            const res = await axios.get(`${API_URL}/setup/countries`, { headers });
            setCountries(res.data);
        } catch (e) { console.error(e); }
    };

    const fetchProjects = async () => {
        try {
            const res = await axios.get(`${API_URL}/it-projects`, { headers });
            setProjects(res.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const handleCreate = async () => {
        try {
            await axios.post(`${API_URL}/it-projects`, newProject, { headers });
            setOpen(false);
            fetchProjects();
        } catch (e) { console.error(e); }
    };

    // Helper functions moved outside

    const handleExportAll = async () => {
        try {
            let query = `?status=${filter}&search=${search}`;
            const response = await axios.get(`${API_URL}/it-projects/export-all-pdf${query}`, {
                headers,
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Global_Projects_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (e) {
            console.error("Export failed", e);
            const msg = e.response?.data?.error || 'Failed to export Global PDF';
            try {
                if (e.response?.data instanceof Blob) {
                    const text = await e.response.data.text();
                    const json = JSON.parse(text);
                    if (json.error) alert(json.error);
                    else alert(msg);
                } else {
                    alert(msg);
                }
            } catch {
                alert(msg);
            }
        }
    };

    const [assignedToMe, setAssignToMe] = useState(true);
    const [selectedCountries, setSelectedCountries] = useState([]);

    // Get current user from token
    const getCurrentUser = () => {
        if (!token) return null;
        try {
            return JSON.parse(atob(token.split('.')[1]));
        } catch (e) {
            return null;
        }
    };
    const currentUser = getCurrentUser();

    // ... existing useEffect ... (no change needed here really, but context)

    const filtered = projects.filter(p => {
        const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
            (p.ref_number && p.ref_number.toLowerCase().includes(search.toLowerCase()));

        const matchesStatus = filter === 'All' || p.status === filter;
        const matchesCountry = selectedCountries.length === 0 || selectedCountries.includes(p.country_name);
        const matchesAssign = !assignedToMe || (p.assigned_users && p.assigned_users.some(u => u.id === currentUser?.user_id));

        return matchesSearch && matchesStatus && matchesCountry && matchesAssign;
    });

    if (loading) return <Loading message="Loading IT Projects..." />;

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            {/* Header Toolbar */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>

                    {/* Left: Filters */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', md: 'auto' } }}>
                        <Chip
                            icon={<PersonIcon />}
                            label="My Projects"
                            onClick={() => setAssignToMe(!assignedToMe)}
                            color={assignedToMe ? "primary" : "default"}
                            variant={assignedToMe ? "filled" : "outlined"}
                            sx={{ fontWeight: 'bold' }}
                        />
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <Select
                                multiple
                                value={selectedCountries}
                                onChange={(e) => setSelectedCountries(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                                displayEmpty
                                variant="outlined"
                                renderValue={(selected) => {
                                    if (selected.length === 0) {
                                        return <Typography color="textSecondary" sx={{ fontSize: '0.875rem' }}>All Countries</Typography>;
                                    }
                                    return selected.join(', ');
                                }}
                                sx={{ borderRadius: 2 }}
                            >
                                <MenuItem disabled value="">
                                    <Typography variant="body2">Select Countries</Typography>
                                </MenuItem>
                                {countries.map(c => (
                                    <MenuItem key={c.id} value={c.name}>
                                        <Checkbox checked={selectedCountries.indexOf(c.name) > -1} size="small" />
                                        {c.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Right: Actions */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', md: 'auto' }, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <TextField
                            placeholder="Search..."
                            size="small"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                                sx: { borderRadius: 5, bgcolor: 'grey.50' }
                            }}
                            sx={{ width: { xs: '100%', sm: 250 } }}
                        />

                        <Button
                            variant="outlined"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={handleExportAll}
                            sx={{ borderRadius: 5, whiteSpace: 'nowrap', textTransform: 'none' }}
                        >
                            Export
                        </Button>

                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, newMode) => { if (newMode) setViewMode(newMode); }}
                            size="small"
                            sx={{ borderRadius: 2 }}
                        >
                            <ToggleButton value="grid" aria-label="grid view">
                                <ViewModuleIcon />
                            </ToggleButton>
                            <ToggleButton value="list" aria-label="list view">
                                <ViewListIcon />
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setOpen(true)}
                            sx={{ borderRadius: 5, whiteSpace: 'nowrap', textTransform: 'none', px: 3 }}
                        >
                            New Project
                        </Button>
                    </Box>
                </Box>

                {/* Status Tabs */}
                <Box sx={{ mt: 2, display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
                    {['All', 'Active', 'In Progress', 'Completed', 'On Hold'].map(f => (
                        <Chip
                            key={f}
                            label={f}
                            onClick={() => setFilter(f)}
                            clickable
                            color={filter === f ? 'primary' : 'default'}
                            variant={filter === f ? 'filled' : 'outlined'}
                            sx={{ fontWeight: 500 }}
                        />
                    ))}
                </Box>
            </Paper>

            {viewMode === 'grid' ? (
                <Grid container spacing={3}>
                    {filtered.map(project => (
                        <Grid item xs={12} md={4} lg={3} key={project.id}>
                            <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 1, cursor: 'pointer', transition: '0.3s', '&:hover': { boxShadow: 4 } }} onClick={() => navigate(`/it-projects/${project.id}`)}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Box sx={{ overflow: 'hidden', mr: 1 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', color: 'primary.main' }}>{project.ref_number}</Typography>
                                            <Typography variant="h6" sx={{ fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{project.name}</Typography>
                                            <Typography variant="caption" color="textSecondary">{project.country_name}</Typography>
                                        </Box>
                                        <Chip label={project.status} size="small" color={getStatusColor(project.status)} sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }} />
                                    </Box>

                                    <Box sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="caption" color="textSecondary">Progress</Typography>
                                            <Typography variant="caption" fontWeight="bold">{calculateProgress(project.tasks)}%</Typography>
                                        </Box>
                                        <LinearProgress variant="determinate" value={calculateProgress(project.tasks)} sx={{ height: 6, borderRadius: 3 }} />
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}>
                                            {project.assigned_users && project.assigned_users.map(u => (
                                                <Avatar key={u.id} alt={u.username} src="/static/images/avatar/1.jpg" title={u.full_name || u.username}>
                                                    {u.username.charAt(0).toUpperCase()}
                                                </Avatar>
                                            ))}
                                        </AvatarGroup>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="caption" color="textSecondary">Due: {project.time_limit ? new Date(project.time_limit).toLocaleDateString() : 'N/A'}</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getPriorityColor(project.priority) }} />
                                            <Typography variant="caption" color="textSecondary">{project.priority}</Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
                    <Table>
                        <TableHead sx={{ bgcolor: 'grey.50' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Reference</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Country</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Team</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Tasks</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Due Date</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Progress</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Priority</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.map(p => (
                                <ProjectRow key={p.id} project={p} navigate={navigate} />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>New IT Project</DialogTitle>
                <DialogContent>
                    <TextField fullWidth label="Project Name" margin="normal" value={newProject.name} onChange={e => setNewProject({ ...newProject, name: e.target.value })} />
                    <TextField fullWidth label="Description" margin="normal" multiline rows={3} value={newProject.description} onChange={e => setNewProject({ ...newProject, description: e.target.value })} />
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Status</InputLabel>
                                <Select value={newProject.status} label="Status" onChange={e => setNewProject({ ...newProject, status: e.target.value })}>
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="In Progress">In Progress</MenuItem>
                                    <MenuItem value="Completed">Completed</MenuItem>
                                    <MenuItem value="On Hold">On Hold</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Priority</InputLabel>
                                <Select value={newProject.priority} label="Priority" onChange={e => setNewProject({ ...newProject, priority: e.target.value })}>
                                    <MenuItem value="High">High</MenuItem>
                                    <MenuItem value="Medium">Medium</MenuItem>
                                    <MenuItem value="Low">Low</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Country</InputLabel>
                                <Select value={newProject.country_id} label="Country" onChange={e => setNewProject({ ...newProject, country_id: e.target.value })}>
                                    <MenuItem value=""><em>Global</em></MenuItem>
                                    {countries.map(c => (
                                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Start Date"
                                type="date"
                                margin="normal"
                                InputLabelProps={{ shrink: true }}
                                value={newProject.start_date}
                                onChange={e => setNewProject({ ...newProject, start_date: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Deadline"
                                type="date"
                                margin="normal"
                                InputLabelProps={{ shrink: true }}
                                value={newProject.time_limit}
                                onChange={e => setNewProject({ ...newProject, time_limit: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreate} variant="contained">Create</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default ITProjects;

function ProjectRow({ project, navigate }) {
    const [open, setOpen] = useState(false);

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }} onClick={() => navigate(`/it-projects/${project.id}`)}>
                <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>{project.ref_number}</TableCell>
                <TableCell>{project.name}</TableCell>
                <TableCell>{project.country_name}</TableCell>
                <TableCell>
                    <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}>
                        {project.assigned_users && project.assigned_users.map(u => (
                            <Avatar key={u.id} alt={u.username} title={u.full_name || u.username}>
                                {u.username.charAt(0).toUpperCase()}
                            </Avatar>
                        ))}
                    </AvatarGroup>
                </TableCell>
                <TableCell onClick={(e) => { e.stopPropagation(); setOpen(!open); }} sx={{ cursor: 'pointer' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
                        >
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {project.tasks ? `${project.tasks.filter(t => t.completed).length}/${project.tasks.length}` : '0/0'}
                        </Typography>
                    </Box>
                </TableCell>
                <TableCell><Chip label={project.status} size="small" color={getStatusColor(project.status)} variant="outlined" /></TableCell>
                <TableCell>{project.time_limit ? new Date(project.time_limit).toLocaleDateString() : '-'}</TableCell>
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LinearProgress variant="determinate" value={calculateProgress(project.tasks)} sx={{ width: 100, mr: 1, height: 6, borderRadius: 3 }} />
                        <Typography variant="caption">{calculateProgress(project.tasks)}%</Typography>
                    </Box>
                </TableCell>
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getPriorityColor(project.priority) }} />
                        {project.priority}
                    </Box>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2, ml: 4 }}>
                            <Typography variant="subtitle2" gutterBottom component="div" sx={{ fontWeight: 'bold' }}>
                                Tasks ({project.tasks ? project.tasks.length : 0})
                            </Typography>
                            {project.tasks && project.tasks.length > 0 ? (
                                <Table size="small" aria-label="tasks">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Title</TableCell>
                                            <TableCell>Due Date</TableCell>
                                            <TableCell>Assigned To</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {project.tasks.map((task) => (
                                            <TableRow key={task.id}>
                                                <TableCell component="th" scope="row">
                                                    {task.completed ? <CheckCircleIcon color="success" fontSize="small" /> : <RadioButtonUncheckedIcon color="disabled" fontSize="small" />}
                                                </TableCell>
                                                <TableCell>{task.title}</TableCell>
                                                <TableCell>{task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}</TableCell>
                                                <TableCell>{task.assigned_to_name || '-'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <Typography variant="caption" color="textSecondary">No tasks found.</Typography>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
}
